import React, { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Payperiod from "../../components/timesheet/Payperiod";
import Employee from "../../components/timesheet/Employee";
import { useSidebarData } from "../../hooks/sidebarData";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Swal from "sweetalert2";
import axios from "../../api/axios";
import axiosaim from "../../api/axiosaim";
import toast from "../../components/sweetalert/Sweetalert";
import { destroySession } from "../../helpers/Session";

const Grandchild = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setSidebarData } = useSidebarData();

    const timeoutRef = useRef(null);
    const [hasEmployeeTimesheets, setHasEmployeeTimesheets] = useState(false);

    const accesstoken = sessionStorage.getItem('accesstoken');
    const aimtoken = sessionStorage.getItem("aimtoken") || "";
    const uname = sessionStorage.getItem('uname') || '';
    const email = sessionStorage.getItem('email');
    const department = sessionStorage.getItem('department');
    const departmentslug = sessionStorage.getItem('departmentslug');
    const breadcrumbChild = sessionStorage.getItem('timesheetChild') || 'N/A';
    const breadcrumbChildSlug = sessionStorage.getItem('timesheetChildSlug') || '';
    const breadcrumbGrandchild = sessionStorage.getItem('timesheetGrandchild') || 'N/A';
    const breadcrumbPayperiod = sessionStorage.getItem('timesheetPayperiod') || 'Pay Period';
    const breadcrumbPayperiodId = sessionStorage.getItem('timesheetPayperiodId');

    const handleClearSidebarData = () => {
        setSidebarData('state', false);
        setSidebarData('key', '');
        setSidebarData('label', '');
        setSidebarData('data', []);
    };
    
    const [accessibleDepartments, setAccessibleDepartments] = useState([]);

    const { data: role } = useQuery({
        queryKey: ['userrole']
    });

    const { data: manageddepartments = [] } = useQuery({
        queryKey: ['managedDepartments', uname]
    });

    const { data: managedadjustments = [] } = useQuery({
        queryKey: ['managedAdjustments', uname]
    });

    const { data: manageddepartmentsAIM = [], isLoading: loadingAIM } = useQuery({
        queryKey: ["managedAIMDepartments", uname],
        queryFn: async () => {
            const res = await axiosaim.get(`getmanageddepartments`, {
                headers: { "X-Aim-Token": aimtoken },
            });
            return res.data;
        },
        enabled: !!uname && !!aimtoken,
        retry: 1
    });

    const isHeadOfThisDepartment = Array.isArray(manageddepartmentsAIM) &&
        manageddepartmentsAIM.some(dept => dept.department_code === departmentslug);

    useEffect(() => {
        const combineDepartments = [...manageddepartments, ...managedadjustments];
        const finalDepartments = combineDepartments.filter((value, index, self) => (
            index === self.findIndex((d) => (
                d.department_code === value.department_code && d.department === value.department
            ))
        ));

        if (JSON.stringify(finalDepartments) !== JSON.stringify(accessibleDepartments)) {
            setAccessibleDepartments(finalDepartments);
        }
    }, [manageddepartments, managedadjustments, accessibleDepartments]);

    const { data: status, isLoadingCutoffApproval } = useQuery({
        queryKey: ['departmentCutoff'],
        queryFn: async () => {
            const res = await axios.get(`payperiods/departments?department=${departmentslug}&payperiod=${breadcrumbPayperiodId}`, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        enabled: !!departmentslug && !!breadcrumbPayperiodId,
        retry: 1
    });

    const submitDepartmentDtrMutation = useMutation({
        mutationFn: async (departmentData) => {
            const res = await axios.post('payperiods/departments', departmentData, {
                headers: { 'Authorization': `Bearer ${accesstoken}` }
            });
            return res.data.response;
        },
        onSuccess: (data) => {
            toast.fire({ icon: 'success', title: data });
            queryClient.invalidateQueries(['departmentCutoff']);
        },
        onError: (error) => {
            const errorMessage = error.response?.data.response || 'Something went wrong while sending department timesheets for approval!';
            toast({ icon: 'error', title: errorMessage });
            if (error.response?.data.status === 'inactive') {
                destroySession();
                clearTimeout(timeoutRef.current);
                timeoutRef.current = setTimeout(() => { navigate('/error'); }, 1000);
            }
        }
    });
    
    const handleSubmitDepartmentDTR = () => {
        Swal.fire({
            text: `Are you sure you want to submit your department timesheets for ${breadcrumbPayperiod}? Please note that only approved timesheets will be sent to the approving officer.`,
            icon: 'warning',
            allowOutsideClick: false,
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            showLoaderOnConfirm: true,
            preConfirm: async (result) => {
                if (result) {
                    await submitDepartmentDtrMutation.mutate({
                        uname,
                        email,
                        department: department,
                        departmentcode: departmentslug,
                        payperiod: breadcrumbPayperiod,
                        payperiodid: breadcrumbPayperiodId
                    });
                }
            }
        });
    }


    return (
        <div className="grandchild">
            <div className="page-header">
                <div className='title with-2-cols'>
                    <div className="left-panel">
                        <h1>Timesheets</h1>
                        <ul className="breadcrumbs">
                            <li>
                                <NavLink to='/dtr/my-dtr' onClick={handleClearSidebarData}>DTR</NavLink>
                            </li>
                            <li>
                                <NavLink to='/dtr/timesheets' onClick={handleClearSidebarData}>Timesheets</NavLink>
                            </li>
                            <li>
                                <NavLink to={`/dtr/timesheets/${breadcrumbChildSlug}`} onClick={handleClearSidebarData}>
                                    <span>{breadcrumbChild}</span>
                                </NavLink>
                            </li>
                            <li>
                                <span>{breadcrumbGrandchild}</span>
                            </li>
                        </ul>
                    </div>

                    {isHeadOfThisDepartment && hasEmployeeTimesheets && (
                            <div className={`right-panel ${isLoadingCutoffApproval && ' loading'}`}>
                                {
                                    isLoadingCutoffApproval
                                     ? (
                                        <h3>Loading...</h3>
                                     ) : (
                                        status === 0
                                            ? (
                                                <h3 className="pending">Submitted</h3>
                                            ) : (
                                                status === 1
                                                    ? (
                                                        <h3 className="approved">Submitted</h3>
                                                    ) : (
                                                        status === 2
                                                            ? (
                                                                <h3 className="disapproved">Disapproved</h3>
                                                            ) : (
                                                                <button type="button" onClick={handleSubmitDepartmentDTR}>
                                                                    <span>SUBMIT</span>
                                                                    <i className="fa fa-send"></i>
                                                                </button>
                                                            )
                                                    )
                                            )
                                        )
                                }
                            </div>
                        )
                    }
                </div>
            </div>

            <div className="content">
                <div className="files">
                    {(role === 'admin' || role === 'management') || manageddepartments?.length > 1 || managedadjustments?.length > 1
                        ? <Payperiod />
                        : <Employee
                            isDepartmentHead={isHeadOfThisDepartment}
                            onEmployeeTimesheetsChange={setHasEmployeeTimesheets}
                          />
                    }
                </div>
            </div>
        </div>
    );
}

export default Grandchild;